"use client";

import type { FestivalQuestion } from "./quiz-data";

type Props = {
  question: FestivalQuestion;
  questionIndex: number;
  totalQuestions: number;
  selectedAnswerId: string | undefined;
  onSelect: (answerId: string) => void;
  onBack: () => void;
};

export function QuestionScreen({
  question,
  questionIndex,
  totalQuestions,
  selectedAnswerId,
  onSelect,
  onBack,
}: Props) {
  const progressPct = (questionIndex / totalQuestions) * 100;

  return (
    <section className="px-5 md:px-10 py-10 md:py-16">
      <div className="max-w-[560px] mx-auto">
        {/* Progress bar */}
        <div className="h-1 bg-sand rounded-full mb-6 overflow-hidden">
          <div
            className="h-full bg-gold rounded-full transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Nav row */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onBack}
            disabled={questionIndex === 0}
            className="text-[13px] text-text-muted hover:text-navy transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Go to previous question"
          >
            &larr; Back
          </button>
          <span className="text-[13px] text-text-muted">
            Question {questionIndex + 1} of {totalQuestions}
          </span>
        </div>

        {/* Prompt */}
        <h2 className="font-serif text-[26px] md:text-[32px] text-navy leading-[1.2] mb-6">
          {question.prompt}
        </h2>

        {/* Answer buttons */}
        <div className="space-y-3">
          {question.answers.map((answer) => {
            const isSelected = answer.id === selectedAnswerId;
            return (
              <button
                key={answer.id}
                onClick={() => onSelect(answer.id)}
                className={[
                  "w-full text-left px-4 py-3.5 rounded-sm border text-[14px] md:text-[15px] leading-[1.5] transition-colors",
                  isSelected
                    ? "border-gold bg-gold/5 text-navy font-medium"
                    : "border-border-light bg-white text-text-muted hover:border-gold/50 hover:text-navy",
                ].join(" ")}
              >
                <span className="flex items-start justify-between gap-3">
                  <span>{answer.label}</span>
                  {isSelected && (
                    <span className="text-gold flex-shrink-0 mt-0.5" aria-hidden>
                      &#10003;
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
